'use client'

import { useInfiniteQuery } from '@tanstack/react-query'
import type { ICategory } from '@/app/[locale]/_types'
import { getCategoryWithProductsList } from '@/app/[locale]/_service/category.service'
import { flattenCategoriesWithChildren } from '../server-utils'

interface ProductsPageData {
  flat_categories: ICategory[]
  categories: ICategory[]
  count?: number
  pageParam: number
}

interface ProductsQueryResult {
  flat_categories: ICategory[]
  categories: ICategory[]
  count?: number
}

// Categories per page - optimized for initial load
const CATEGORIES_PER_PAGE = 30

/**
 * Hook for fetching categories with their metadata.
 * Products are NOT fetched here - they're lazily loaded per-section.
 *
 * Performance optimizations:
 * - Categories cached for 5 minutes (staleTime)
 * - Data kept in cache for 15 minutes (gcTime)
 * - No refetch on window focus
 * - Infinite scroll pagination for additional categories
 *
 * @param hasInitialData - If true, assumes page 1 was fetched on server and starts from page 2.
 *                         If false, starts from page 1 to avoid skipping categories.
 */
export function useCategoriesQuery(
  shipper_id: string,
  platform_id: string,
  effectiveMenuId: string | undefined,
  deliveryType: string,
  is_hall: boolean,
  enabled: boolean = false,
  hasInitialData: boolean = false,
  source_id?: string,
  useDynamicLanguage = false,
  isShipperResolved = true
) {
  // Start from page 1 if no initial data (to avoid skipping categories)
  // Otherwise start from page 2 (assuming server provided page 1)
  const initialPage = hasInitialData ? 2 : 1

  return useInfiniteQuery({
    queryKey: [
      'categories',
      shipper_id,
      effectiveMenuId,
      is_hall,
      deliveryType,
      source_id,
      useDynamicLanguage,
    ],
    queryFn: async ({ pageParam = initialPage }): Promise<ProductsPageData> => {
      // Use getCategoryWithProductsList for client-side to get products included
      // This matches what the server uses, ensuring consistent data structure
      const response = await getCategoryWithProductsList(
        {
          page: pageParam,
          limit: CATEGORIES_PER_PAGE,
          is_active: true,
          has_product: true,
          menu_id: effectiveMenuId,
          delivery_type: deliveryType,
          aggregator_id: source_id,
        },
        shipper_id,
        platform_id,
        useDynamicLanguage
      )

      const categories = response?.data?.categories || []
      const flat_categories = await flattenCategoriesWithChildren(categories)

      return {
        categories,
        flat_categories,
        count: +response.data.count,
        pageParam,
      }
    },
    initialPageParam: initialPage,
    getNextPageParam: (lastPage: ProductsPageData): number | undefined => {
      const nextPage = lastPage.pageParam + 1
      // Only fetch more if we got a full page of results
      return lastPage.categories.length >= CATEGORIES_PER_PAGE
        ? nextPage
        : undefined
    },
    select: (data): ProductsQueryResult => ({
      categories: data.pages.flatMap((page) => page.categories),
      flat_categories: data.pages.flatMap((page) => page.flat_categories),
      count: data.pages[0]?.count,
    }),
    // Extended cache times for categories (they change less frequently)
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 15 * 60 * 1000, // 15 minutes
    enabled: Boolean(shipper_id) && isShipperResolved && enabled,
    refetchOnWindowFocus: false,
  })
}
